import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { TypeBody } from '../Typography';
import Icon from 'components/global/Icon/Primary';
import Label from '../Label';
import { createMarkup } from 'lib/utils/create-markup';
import { trackEvent } from 'lib/analytics';

const Container = styled.div``;
const AccordionItemContainer = styled.button`
  appearance: none;
  cursor: pointer;
  border: 0;
  border-top: 1px solid var(--outline-neutral);
  padding: var(--spacing-16) 0;
  &:last-child {
    border-bottom: 1px solid var(--outline-neutral);
  }
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  background: transparent;
  color: var(--text-primary);
`;
const AccordionItemTitle = styled(TypeBody)`
  --fw: 500;
`;
const AccordionItemContent = styled(TypeBody)``;
const AccordionTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;
const AccordionTitleInner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-8);
`;
const AccordionLabel = styled(Label)`
  width: fit-content;
`;

const OpenSidebarButton = styled.span`
  display: inline-flex;
  margin-top: 1rem;
  align-items: center;
  gap: 0.5rem;
  text-decoration: underline;
  font-weight: 500;
  color: var(--fill-brand);
  cursor: pointer;
`;

type AccordionItemProps = {
  title: string;
  content: React.ReactNode | string;
  expanded?: boolean;
  onClick: () => void;
  showLabel?: boolean;
};

const AccordionItem = ({
  title,
  content,
  expanded = false,
  onClick,
  showLabel = false,
  displayContentAsHTML,
  displayTitleAsHTML = false,
  titleInnerExtraClasses = '',
  contentCTA = false,
  contentCTACallback = false,
}: AccordionItemProps) => {
  return (
    <AccordionItemContainer data-is-open={expanded} onClick={onClick}>
      <AccordionTitleContainer>
        <AccordionTitleInner className={`${titleInnerExtraClasses}`}>
          {displayTitleAsHTML && (
            <AccordionItemTitle dangerouslySetInnerHTML={createMarkup(title)} />
          )}
          {!displayTitleAsHTML && (
            <AccordionItemTitle>{title}</AccordionItemTitle>
          )}
          {showLabel && (
            <AccordionLabel size='small' color='seedgreen'>
              DM-02® Exclusive
            </AccordionLabel>
          )}
        </AccordionTitleInner>
        {expanded ? (
          <Icon name='minus' size='md' />
        ) : (
          <Icon name='plus' size='md' />
        )}
      </AccordionTitleContainer>
      {expanded && !displayContentAsHTML && (
        <AccordionItemContent>{content}</AccordionItemContent>
      )}
      {expanded && displayContentAsHTML && (
        <div dangerouslySetInnerHTML={createMarkup(content)}></div>
      )}
      {expanded && contentCTA && typeof contentCTACallback === 'function' && (
        <OpenSidebarButton
          onClick={(event) => {
            event.stopPropagation();
            contentCTACallback();
          }}
          tabIndex={0}
          role='button'
        >
          {contentCTA}
          <Icon name='arrow-right' size='md' />
        </OpenSidebarButton>
      )}
    </AccordionItemContainer>
  );
};

type AccordionProps = {
  items: {
    title: string;
    content: React.ReactNode | string;
    showLabel?: boolean;
  }[];
};

const Accordion = ({
  items,
  displayContentAsHTML = false,
  displayTitleAsHTML = false,
  initialItem = 0,
  titleInnerExtraClasses = '',
  contentCTA = false,
  contentCTACallback = false,
}: AccordionProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(initialItem);
  const expandItem = (index: number) => {
    setSelectedIndex(index === selectedIndex ? -1 : index);

    if (items[index]?.accordionEvent) {
      const event = items[index].accordionEvent;
      const eventType = index === selectedIndex ? 'Contract' : 'Expand';

      trackEvent('Button Clicked', {
        buttonName: `${eventType} ${event.buttonName}`,
        question: event.question,
        location: event.location || 'PDP',
      });
    }
  };

  useEffect(() => {
    setSelectedIndex(initialItem);
  }, [items]);

  return (
    <Container>
      {items.map((item, index) => (
        <AccordionItem
          key={index}
          expanded={index === selectedIndex}
          onClick={() => expandItem(index)}
          displayContentAsHTML={displayContentAsHTML}
          displayTitleAsHTML={displayTitleAsHTML}
          titleInnerExtraClasses={titleInnerExtraClasses}
          contentCTA={contentCTA}
          contentCTACallback={contentCTACallback}
          {...item}
        />
      ))}
    </Container>
  );
};

export default Accordion;
